// Location utility functions for GPS detection and reverse geocoding
import { pincodesByArea } from '../data/usStates';

/**
 * Geocode a city and state to coordinates using Nominatim (OpenStreetMap).
 * @param {string} city
 * @param {string} state
 * @returns {Promise<{latitude: number, longitude: number} | null>}
 */
export const geocodeCityState = async (city, state) => {
  if (!city && !state) return null;
  const query = [city, state].filter(Boolean).join(', ');
  if (!query.trim()) return null;
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`,
      { headers: { 'User-Agent': 'ClubFinder App' } }
    );
    if (!res.ok) return null;
    const data = await res.json();
    const first = data?.[0];
    if (first && first.lat != null && first.lon != null) {
      return { latitude: parseFloat(first.lat), longitude: parseFloat(first.lon) };
    }
    return null;
  } catch (e) {
    console.warn('Geocode failed for', query, e);
    return null;
  }
};

/**
 * Get user's current location using GPS
 * @returns {Promise<{latitude: number, longitude: number}>}
 */
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
};

/**
 * Reverse geocode coordinates to get zip code using Nominatim (OpenStreetMap)
 * @param {number} latitude 
 * @param {number} longitude 
 * @returns {Promise<{zipcode: string, area: string, city: string, state: string}>}
 */
export const reverseGeocode = async (latitude, longitude) => {
  try {
    console.log('Reverse geocoding coordinates:', latitude, longitude);
    
    // Using Nominatim API (free, no API key required)
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1&zoom=18`,
      {
        headers: {
          'User-Agent': 'ClubFinder App' // Required by Nominatim
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`Reverse geocoding failed: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('Reverse geocoding response:', data);
    
    if (data && data.address) {
      const address = data.address;
      const zipcode = address.postcode || '';
      const city = address.city || address.town || address.village || address.municipality || '';
      const state = address.state || address.state_code || '';
      
      // Try multiple address components to find the neighborhood/area name
      // Priority: neighbourhood > suburb > quarter > district > city_district > residential
      let area = '';
      
      // Check various address fields that might contain neighborhood name
      const areaFields = [
        address.neighbourhood,
        address.suburb,
        address.quarter,
        address.district,
        address.city_district,
        address.residential,
        address.village,
        address.town
      ];
      
      // Find the first non-empty field that's not the city name
      for (const field of areaFields) {
        if (field && field.toLowerCase() !== city.toLowerCase() && field.length > 0) {
          area = field;
          break;
        }
      }
      
      // If still no area, try parsing from display_name
      if (!area && data.display_name) {
        const parts = data.display_name.split(',');
        // Usually format is: "Area, City, State, Country"
        // Try to extract area from first part if it's different from city
        if (parts.length > 1) {
          const potentialArea = parts[0].trim();
          const potentialCity = parts[1].trim();
          if (potentialArea && potentialArea.toLowerCase() !== potentialCity.toLowerCase()) {
            area = potentialArea;
          }
        }
      }
      
      return {
        zipcode,
        area: area || '',
        city: city || '',
        state: state || '',
        fullAddress: data.display_name || ''
      };
    }
    
    throw new Error('Unable to reverse geocode location');
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    throw error;
  }
};

/**
 * Find Seattle area/neighborhood from zip code
 * @param {string} zipcode 
 * @returns {string|null}
 */
export const findAreaFromZipcode = (zipcode) => {
  for (const [area, zipcodes] of Object.entries(pincodesByArea)) {
    if (zipcodes.includes(zipcode)) {
      return area;
    }
  }
  return null;
};

/**
 * Get state code from state name
 * @param {string} stateName 
 * @returns {string}
 */
const getStateCode = (stateName) => {
  const stateMap = {
    'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR', 'california': 'CA',
    'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE', 'florida': 'FL', 'georgia': 'GA',
    'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA',
    'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
    'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO',
    'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
    'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH',
    'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
    'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT', 'vermont': 'VT',
    'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY'
  };
  
  const normalized = stateName.toLowerCase();
  return stateMap[normalized] || stateName.toUpperCase().substring(0, 2);
};

/**
 * Detect user location and return formatted location data
 * @returns {Promise<{state: string, city: string, area: string, pincode: string}>}
 */
export const detectUserLocation = async () => {
  try {
    console.log('Starting location detection...');
    
    // Get GPS coordinates
    const { latitude, longitude } = await getCurrentLocation();
    console.log('GPS coordinates obtained:', latitude, longitude);
    
    // Reverse geocode to get address
    const geoData = await reverseGeocode(latitude, longitude);
    console.log('Reverse geocoding data:', geoData);
    
    // Extract state code from state name
    let stateCode = geoData.state;
    if (stateCode && stateCode.length > 2) {
      stateCode = getStateCode(stateCode);
    }
    
    // Use the area directly from reverse geocoding (no predefined mappings)
    // If area is not found, try to extract from full address
    let area = geoData.area;
    
    // If no area from address components, try to extract from display_name
    if (!area && geoData.fullAddress) {
      const parts = geoData.fullAddress.split(',');
      // Look for neighborhood name in the address parts
      for (let i = 0; i < Math.min(parts.length, 3); i++) {
        const part = parts[i].trim();
        // Skip if it's a number (zipcode) or common words
        if (part && !part.match(/^\d+$/) && 
            !['united states', 'usa', 'us'].includes(part.toLowerCase())) {
          // Check if it's not the city name
          if (part.toLowerCase() !== geoData.city.toLowerCase()) {
            area = part;
            break;
          }
        }
      }
    }
    
    const result = {
      state: stateCode || '',
      city: geoData.city || '',
      area: area || '',
      pincode: geoData.zipcode || ''
    };
    
    console.log('Location detection result:', result);
    
    // Validate that we have at least state and city
    if (!result.state || !result.city) {
      throw new Error(`Incomplete location data: state=${result.state}, city=${result.city}`);
    }
    
    return result;
  } catch (error) {
    console.error('Location detection error:', error);
    console.error('Error stack:', error.stack);
    // Re-throw with more context
    if (error.message) {
      throw new Error(`Location detection failed: ${error.message}`);
    }
    throw error;
  }
};

